import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { TYPESENSE_COLLECTIONS } from 'src/app/shared/constants/typesense-collections';
import { environment } from 'src/environments/environment';

@Injectable({
   providedIn: 'root',
})
export class StockOverviewDataService {
   private stockApiUrl = `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.STOCKS_DATA}/documents/`;
   private indicesApiUrl = `${environment.nove_infomavan_v2.url}/collections/${TYPESENSE_COLLECTIONS.STOCK_INDICES}/documents/search`;
   private ipoUrl = `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.IPO_PROFILE_COLLECTION}/documents/search`;

   private ipoUrl2 = `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.COMPLIANCE_COLLECTION_3}/documents/`;
   private ipoDisclaimer = `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.IPO_DISCLAIMER}/documents/search?filter_by=page:=ipo_calender&q=*`;
   private apiKey = environment.typeSense.searchApiKey; // Ensure API key is set correctly
   private apiKey2 = environment.nove_infomavan_v2.searchApiKey;
   private apiKey3 = environment.typeSense.searchApiKey;
   private apiKey4 = environment.typeSense.searchApiKey;

   private earningsCalendarUrl = `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.EARNINGS_CALENDAR_ONE}/documents/search`;

   constructor(private http: HttpClient) {}

   async getStockData(symbol: string): Promise<any> {
      try {
         const response: any = await lastValueFrom(
            this.http.get(`${this.stockApiUrl}${symbol}`)
         );
         return response;
      } catch (error) {
         throw error;
      }
   }

   async getStockIndices(countryCode: string): Promise<any> {
      try {
         const params = new HttpParams()
            .set('per_page', 1)
            .set('page', 1)
            .set('q', '*')
            .set('filter_by', `country_code:${countryCode} && priority_level:=1`);

         const response: any = await lastValueFrom(
            this.http.get(this.indicesApiUrl, { params })
         );
         return response;
      } catch (error) {
         throw error;
      }
   }

   async getIpoData(
      sortBy?: string,
      sortOrder?: 'asc' | 'desc',
      shariaCompliance?: string[],
      ranking_v2?: string[],
      perPage: number = 15,
      page: number = 1,
      status?: string,
      country?: string,
      timestampFilter?: string
   ): Promise<{ hits: any[]; found: number }> {
      try {
         const sortParam = sortBy && sortOrder ? `${sortBy}:${sortOrder}` : undefined;
         const filterQueries: string[] = [];

         if (shariaCompliance?.length) {
            const shariaQuery = shariaCompliance
               .map((value) => `sharia_compliance:=${value}`)
               .join(' || ');
            filterQueries.push(`(${shariaQuery})`);
         } else {
            filterQueries.push('(sharia_compliance:=COMPLIANT || sharia_compliance:=NON_COMPLIANT)');
         }

         if (ranking_v2?.length) {
            const ratingQuery = ranking_v2
               .map((value) => `ranking_v2:=${value}`)
               .join(' || ');
            filterQueries.push(`(${ratingQuery})`);
         }

         if (status) filterQueries.push(`ipo_status:=${status}`);
         if (country) filterQueries.push(`country:=${country}`);
         if (timestampFilter) filterQueries.push(timestampFilter);

         const filterBy = filterQueries.join(' && ');

         // const headers = new HttpHeaders()
         //    .set('x-typesense-api-key', this.apiKey3);

         let params = new HttpParams()
            .set('q', '*')
            .set('page', page)
            .set('per_page', perPage);

         if (sortParam) {
            params = params.set('sort_by', sortParam);
         }
         if (filterBy) {
            params = params.set('filter_by', filterBy);
         }

         const response: any = await lastValueFrom(
            this.http.get(this.ipoUrl, { params })
         );

         return {
            hits: response['hits'] || [],
            found: response['found'] || 0,
         };
      } catch (error) {
         console.error('Error fetching IPO data:', error);
         throw error;
      }
   }

   private freeIpoCache: string[] = [];

   async getFreeIpoTickers(): Promise<string[]> {
      try {
         // Check if cache exists and is still valid
         if (this.freeIpoCache.length) {
            return this.freeIpoCache;
         }

         const params = new HttpParams()
            .set('q', '*')
            .set('filter_by', 'ipo_status:=Upcoming')
            .set('sort_by', 'marketCapitalization:desc')
            .set('per_page', '3')
            .set('page', '1');

         const response: any = await lastValueFrom(
            this.http.get(this.ipoUrl, { params })
         );

         const tickers = response.hits.map((hit: any) => hit.document.ticker);

         // Update cache
         this.freeIpoCache = tickers

         return tickers;
      } catch (error) {
         console.error('Error fetching free IPO tickers:', error);
         return [];
      }
   }

   async getIpoComplianceReport(ticker: string) {
      try {
         // const headers = new HttpHeaders()
         //    .set('x-typesense-api-key', this.apiKey3)
         //    .set('Content-Type', 'application/json');

         const response: any = await lastValueFrom(
            this.http.get(`${this.ipoUrl2}${ticker}`)
         );
         return response;
      } catch (error) {
         throw error;
      }
   }

   async getIpoDisclaimer() {
      try {
         // const headers = new HttpHeaders()
         //    .set('x-typesense-api-key', this.apiKey3)
         //    .set('Content-Type', 'application/json');

         const response: any = await lastValueFrom(
            this.http.get(this.ipoDisclaimer)
         );
         return response['hits'][0].document.text;
      } catch (error) {
         throw error;
      }
   }

   async getSpecificIpo(ticker: string) {
      try {
         // const headers = new HttpHeaders()
         //    .set('x-typesense-api-key', this.apiKey3)
         //    .set('Content-Type', 'application/json');

         const params = new HttpParams()
            .set('q', '*')
            .set('filter_by', `company_symbol:=${ticker}`)
            .set('page', 1)
            .set('per_page', 250);

         const response: any = await lastValueFrom(
            this.http.get(this.ipoUrl, { params })
         );

         return response['hits'][0]?.document;
      } catch (error) {
         console.error('Typesense IPO fetch error:', error);
         throw error;
      }
   }

   async getIpoDataByTicker(
      ticker: string
   ): Promise<{ hits: any[]; found: number }> {
      try {
         // const headers = new HttpHeaders()
         //    .set('x-typesense-api-key', this.apiKey3);

         const params = new HttpParams()
            .set('q', '*')
            .set('filter_by', `ticker:=${ticker}`)
            .set('page', 1)
            .set('per_page', 15);

         const response: any = await lastValueFrom(
            this.http.get(this.ipoUrl, { params })
         );

         return {
            hits: response['hits'] || [],
            found: response['found'] || 0,
         };
      } catch (error) {
         console.error('Error fetching IPO data by ticker:', error);
         throw error;
      }
   }

   async getEarningsCalendar(
      perPage: number = 15,
      page: number = 1,
      sortBy?: string,
      sortOrder?: string,
      date?: string,
      ticker?: string,
      exchangeCode: string = 'US' // <-- default to US
   ): Promise<any> {
      let params = new HttpParams()
         .set('per_page', perPage)
         .set('page', page)
         .set('q', date || '*')
         .set('query_by', 'date');

      let filterByParts: string[] = [`exchange_code:=${exchangeCode}`]; // <-- dynamic

      if (date) {
         filterByParts.push(`date:=[${date}]`);
      }

      if (ticker) {
         filterByParts.push(`company_symbol:=${ticker}`);
      }

      params = params.set('filter_by', filterByParts.join(' && '));

      if (sortBy && sortOrder) {
         params = params.set('sort_by', `${sortBy}:${sortOrder}`);
      }

      const response: any = await lastValueFrom(
         this.http.get(this.earningsCalendarUrl, { params })
      );

      return response;
   }




   async searchTickers(query: string): Promise<any> {
      const params = new HttpParams()
         .set('q', query)
         .set('per_page', 10)
         .set('query_by', 'company_symbol,name'); // <-- updated here

      const response: any = await lastValueFrom(
         this.http.get(this.earningsCalendarUrl, { params })
      );

      return response;
   }

}
